import type {
  IReference,
  IResolvedTextEditorModel,
} from "@codingame/monaco-vscode-views-service-override";
import type { Uri } from "monaco-editor";
import { useEffect, useState } from "react";
import { ITextModelService, StandaloneServices } from "vscode/services";

export type TextEditorModelResolveStatus =
  | {
      state: "LOADING";
    }
  | {
      state: "READY";
      model: IResolvedTextEditorModel;
    }
  | {
      state: "ERROR";
      error: string;
    };

type InternalTextEditorModelResolveStatus =
  | {
      state: "LOADING";
    }
  | {
      state: "READY";
      modelRef: IReference<IResolvedTextEditorModel>;
    }
  | {
      state: "ERROR";
      error: string;
    };

export function useTextEditorModel(
  resource: Uri,
): TextEditorModelResolveStatus {
  const [modelState, setModelState] =
    useState<InternalTextEditorModelResolveStatus>({ state: "LOADING" });

  useEffect(() => {
    setModelState({ state: "LOADING" });

    const isDisposed = false;
    StandaloneServices.get(ITextModelService)
      .createModelReference(resource)
      .then((modelRef) => {
        if (isDisposed) {
          modelRef.dispose();
          return;
        }

        setModelState({ state: "READY", modelRef });
      })
      .catch((e) => {
        setModelState({ state: "ERROR", error: e.message });
      });

    return () => {
      if (modelState.state === "READY") {
        modelState.modelRef.dispose();
      }
    };
  }, [resource.toString()]);

  if (modelState.state === "READY") {
    return {
      state: "READY",
      model: modelState.modelRef.object,
    };
  }

  return modelState;
}
